import { useContext, useEffect, useRef } from 'react';
import { Icon, type IconProps } from '@cloudscape-design/components';
import { HeroPanelsContext, MENU_COPY, type HeroPanels } from '../../heroPanels';
import { LINKS_COPY } from '../../links/growthPlatformsLinks';
import { LINKS_DRAWER_ID } from '../../links/linksPanelState';

interface PanelSpec {
  icon: IconProps.Name;
  label: string;
  /** The panel's own close button, which takes focus when the panel opens. */
  closeLabel: string;
  controls?: string;
}

const PANELS: Record<keyof HeroPanels, PanelSpec> = {
  links: {
    icon: 'external',
    label: LINKS_COPY.homeButton,
    closeLabel: LINKS_COPY.closeButton,
    controls: LINKS_DRAWER_ID,
  },
  menu: { icon: 'menu', label: MENU_COPY.homeButton, closeLabel: MENU_COPY.close },
};

/**
 * A labeled button in the hero's top corner that opens one of the shell's side panels:
 * "Menu" for the side navigation (ADR 0045), "Helpful External Links" for the links
 * drawer (ADR 0044). On home, Cloudscape's icon-only toggle for that panel is left out
 * while it's closed, so this is the way in. Renders nothing outside the shell.
 */
export default function HeroPanelButton({ panel }: { panel: keyof HeroPanels }) {
  const control = useContext(HeroPanelsContext)?.[panel];
  const spec = PANELS[panel];
  const open = control?.open ?? false;
  const ref = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(open);

  // Do what Cloudscape's own toggle does: opening moves focus to the panel's close
  // button, and closing returns it to the toggle. On home that toggle is this button,
  // which Cloudscape doesn't know about, so focus would otherwise drop to the page.
  useEffect(() => {
    if (!wasOpen.current && open && document.activeElement === ref.current) {
      document.querySelector<HTMLButtonElement>(`button[aria-label="${spec.closeLabel}"]`)?.focus();
    } else if (wasOpen.current && !open && (document.activeElement === document.body || !document.activeElement)) {
      ref.current?.focus();
    }
    wasOpen.current = open;
  }, [open, spec.closeLabel]);

  if (!control) return null;
  return (
    <button
      ref={ref}
      type="button"
      className={`gp-fd-panel-button gp-fd-panel-button-${panel} gp-fd-rise`}
      aria-expanded={open}
      aria-controls={open ? spec.controls : undefined}
      onClick={control.toggle}
    >
      <Icon name={spec.icon} />
      <span>{spec.label}</span>
    </button>
  );
}
